import { Text } from '@chakra-ui/layout';
import { Box, Button, Flex, Heading } from '@chakra-ui/react';
import { useContext } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ErrorMessage } from '@/components/core/inputs/ErrorMessage';
import { Field } from '@/components/core/inputs/Field';
import { AlreadyHaveAccount } from '@/components/SignUpFlow/components/AlreadyHaveAccount';
import { GoogleSocialSignInButton } from '@/components/ui/shared/social/SocialSignInButton/SocialSignInButton';
import { ISignupStepsProps, SignUpContext } from '@/containers/SignUp/components/SignUpContext';
import { EMAIL_REGEX } from '@/utils/constants';

export const UserInfoStep = () => {
  const { setSignUpProps, setCurrentStep, signUpProps } = useContext(SignUpContext);
  const {
    handleSubmit,
    register,
    watch,
    formState: { errors },
  } = useForm({ defaultValues: signUpProps });
  const { t } = useTranslation();

  function onSubmit(props: Partial<ISignupStepsProps>) {
    setSignUpProps((snp) => ({ ...snp, ...props }));
    setCurrentStep((cs) => cs + 1);
  }

  return (
    <Flex w="full" justifyContent="space-around" flexDirection={{ base: 'column', lg: 'row' }}>
      <Flex mb={14} maxW="390px" gap={8} flexDirection="column" flex={1}>
        <Text color="gray.600">{`1 / 3`}</Text>
        <Heading fontSize="5xl">{t('createAccount.basicInfo.title')}</Heading>
        <Text mt={13}>{t('createAccount.basicInfo.message')}</Text>
      </Flex>
      <Flex onSubmit={handleSubmit(onSubmit)} as="form" flexDirection="column" maxW="390px" flex={1}>
        <Flex>
          <GoogleSocialSignInButton actionType="signUp">{t('googleSignUp')}</GoogleSocialSignInButton>
        </Flex>

        <Box my={10} borderBottomWidth="1px" borderColor="gray.200" />

        <Flex flexDirection={{ base: 'column', lg: 'row' }} gap={{ base: 6, lg: 0 }} justifyContent="space-between">
          <Box>
            <Field
              {...register('firstName', { required: { value: true, message: t('form.required') } })}
              label={t('createAccount.basicInfo.firstName')}
              hasValue={Boolean(watch('firstName'))}
              isError={Boolean(errors['firstName'])}
              autoComplete="given-name"
            />
            <Text mt={2} fontSize="sm" color="red.600">
              <ErrorMessage errors={errors} name="firstName" />
            </Text>
          </Box>
          <Box>
            <Field
              {...register('lastName', { required: { value: true, message: t('form.required') } })}
              label={t('createAccount.basicInfo.lastName')}
              hasValue={Boolean(watch('lastName'))}
              isError={Boolean(errors['lastName'])}
              autoComplete="family-name"
            />
            <Text mt={2} fontSize="sm" color="red.600">
              <ErrorMessage errors={errors} name="lastName" />
            </Text>
          </Box>
        </Flex>
        <Box mt={6}>
          <Field
            {...register('email', {
              required: { value: true, message: t('form.required') },
              pattern: {
                value: EMAIL_REGEX,
                message: t('form.invalidEmail'),
              },
            })}
            label={t('createAccount.basicInfo.email')}
            hasValue={Boolean(watch('email'))}
            isError={Boolean(errors['email'])}
            autoComplete="email"
          />
          <Text mt={2} fontSize="sm" color="red.600">
            <ErrorMessage errors={errors} name="email" />
          </Text>
        </Box>
        <Box mt={24}>
          <Button variant="outline" w="130px" type="submit">
            {t('action.next')}
          </Button>
        </Box>

        <AlreadyHaveAccount />
      </Flex>
    </Flex>
  );
};
